import type { PerformanceMode } from './animatedBackgroundSlice'
import type { BlobPosition } from './animationEngine'

export interface RenderingConfig {
  performanceMode: PerformanceMode
  dimensions: { width: number; height: number }
}

export class AnimatedBackgroundRenderingService {
  private canvas: HTMLCanvasElement | null = null
  private ctx: CanvasRenderingContext2D | null = null
  private config: RenderingConfig
  private preRenderedGradients = new Map<string, ImageBitmap>()
  private offscreenCanvas: OffscreenCanvas | null = null
  private gradientUpdateQueued = false

  constructor(config: RenderingConfig) {
    this.config = config
    this.initializeOffscreenCanvas()
  }

  initialize(canvas: HTMLCanvasElement): void {
    this.canvas = canvas
    this.ctx = canvas.getContext('2d')

    if (!this.ctx) {
      throw new Error('Failed to get 2D context from canvas')
    }

    this.updateCanvasSize()
  }

  updateConfig(config: Partial<RenderingConfig>): void {
    const dimensionsChanged =
      config.dimensions &&
      (config.dimensions.width !== this.config.dimensions.width ||
        config.dimensions.height !== this.config.dimensions.height)

    const performanceModeChanged =
      config.performanceMode && config.performanceMode !== this.config.performanceMode

    this.config = { ...this.config, ...config }

    if (dimensionsChanged) {
      this.updateCanvasSize()
    }

    if (performanceModeChanged) {
      this.queueGradientUpdate()
    }
  }

  render(positions: BlobPosition[]): void {
    if (!this.ctx || !this.canvas) return

    this.ctx.clearRect(0, 0, this.config.dimensions.width, this.config.dimensions.height)

    this.ctx.save()
    this.ctx.scale(1, -1)
    this.ctx.translate(0, -this.config.dimensions.height)

    for (const pos of positions) {
      this.renderBlob(pos)
    }

    this.ctx.restore()
  }

  async preRenderGradients(positions: BlobPosition[]): Promise<void> {
    if (!this.offscreenCanvas || this.config.performanceMode === 'low') {
      return
    }

    const newGradients = new Map<string, ImageBitmap>()

    for (const pos of positions) {
      const size = Math.ceil(pos.size)
      const canvas = new OffscreenCanvas(size, size)
      const ctx = canvas.getContext('2d')

      if (!ctx) continue

      ctx.fillStyle = pos.color
      ctx.globalAlpha = 1.0

      ctx.beginPath()
      ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2)
      ctx.fill()

      try {
        const bitmap = await canvas.transferToImageBitmap()
        newGradients.set(pos.id, bitmap)
      } catch (error) {
        console.error('Failed to transfer to image bitmap', error)
      }
    }

    this.preRenderedGradients.forEach((bitmap) => bitmap.close())
    this.preRenderedGradients = newGradients
    this.gradientUpdateQueued = false
  }

  getBlurAmount(): string {
    switch (this.config.performanceMode) {
      case 'high':
        return '64px'
      case 'medium':
        return '32px'
      case 'low':
        return '16px'
      default:
        return '32px'
    }
  }

  dispose(): void {
    this.preRenderedGradients.forEach((bitmap) => bitmap.close())
    this.preRenderedGradients.clear()
    this.offscreenCanvas = null
    this.canvas = null
    this.ctx = null
  }

  needsGradientUpdate(): boolean {
    return this.gradientUpdateQueued
  }

  queueGradientUpdate(): void {
    this.gradientUpdateQueued = true
  }

  private initializeOffscreenCanvas(): void {
    if (typeof OffscreenCanvas !== 'undefined') {
      this.offscreenCanvas = new OffscreenCanvas(1, 1)
    }
  }

  private updateCanvasSize(): void {
    if (!this.canvas) return

    this.canvas.width = this.config.dimensions.width
    this.canvas.height = this.config.dimensions.height
  }

  private renderBlob(pos: BlobPosition): void {
    if (!this.ctx) return

    const preRendered = this.preRenderedGradients.get(pos.id)

    if (preRendered && this.config.performanceMode !== 'low') {
      this.ctx.save()
      this.ctx.globalAlpha = 1.0
      this.ctx.drawImage(
        preRendered,
        Math.floor(pos.x - pos.size / 2),
        Math.floor(pos.y - pos.size / 2)
      )
      this.ctx.restore()
    } else {
      this.renderBlobDirect(pos)
    }
  }

  private renderBlobDirect(pos: BlobPosition): void {
    if (!this.ctx) return

    this.ctx.save()

    this.ctx.fillStyle = pos.color
    this.ctx.globalAlpha = 1.0

    this.ctx.beginPath()
    this.ctx.arc(Math.floor(pos.x), Math.floor(pos.y), pos.size / 2, 0, Math.PI * 2)
    this.ctx.fill()

    this.ctx.restore()
  }
}
